import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Play, RotateCcw, Search, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { LEVEL_RANK } from "@/lib/roninBox";

/**
 * Cue picker — lets the user select which voice cues to use in the workout.
 *
 * UX:
 *   - Per-category sections with a master "all/none" checkbox that drives the children
 *     (categories come dynamically from /api/ronin-box/categories).
 *   - Each cue has its own checkbox so users can unselect individual cues from a category.
 *   - Search filter narrows visible cues but doesn't change the selection state of hidden ones.
 *   - Level chip row above the search lets the user pre-fill the selection by skill level
 *     (Beginner / Intermediate / Advanced / All cues).
 *   - Shown on every Custom-mode workout start; last selection is restored from localStorage.
 *
 * Returns the list of selected cue IDs via `onConfirm(selectedIds, allCues)`.
 */
const LS_KEY = "ronin_box_selected_cue_ids_v1";

const LEVEL_OPTIONS = [
  { id: "custom",       label: "Custom (none)" },
  { id: "beginner",     label: "Beginner" },
  { id: "intermediate", label: "Intermediate" },
  { id: "advanced",     label: "Advanced" },
];

export function RoninCuePicker({ open, mode, initialLevel, onConfirm, onCancel }) {
  const [cues, setCues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set()); // cue.id
  const [activeLevel, setActiveLevel] = useState("custom");
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set()); // category keys
  const [hasHydrated, setHasHydrated] = useState(false);

  // Helper: ids that match (mode + level membership).
  // Custom mode = wildcard (any mode). "all" level = no level filter.
  // Uses the cue's `levels` array (a cue can belong to any combination of levels).
  const idsForLevel = (list, lvl) => {
    const isCustom = mode === "custom";
    return list
      .filter((c) => isCustom || c.category === "atmosphere" || (c.modes || []).includes(mode))
      .filter((c) => {
        if (!lvl || lvl === "all") return true;
        // levels array (preferred) — fall back to derived cumulative from legacy `level`
        const lvls = (c.levels && c.levels.length > 0)
          ? c.levels
          : (c.level === "advanced" ? ["advanced"]
             : c.level === "intermediate" ? ["intermediate", "advanced"]
             : ["beginner", "intermediate", "advanced"]);
        return lvls.includes(lvl);
      })
      .map((c) => c.id);
  };

  // Fetch cues whenever picker opens
  useEffect(() => {
    if (!open) return;
    // Coach asked for the picker to always boot at "Custom · 0 selected" regardless of
    // the mode the user just picked on the start screen. From there they can hit
    // BEG/INT/ADV to bulk-fill, or tick cues individually. Last selection from
    // localStorage is intentionally NOT auto-restored — users want a clean slate every
    // time the picker opens.
    setActiveLevel("custom");
    setSelected(new Set());
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [cuesRes, catsRes] = await Promise.all([
          api.get("/ronin-box/cues"),
          api.get("/ronin-box/categories"),
        ]);
        if (cancelled) return;
        const list = cuesRes.data || [];
        setCues(list);
        setCategories(catsRes.data || []);
        setHasHydrated(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter cues by `mode` (so the picker only shows what's relevant) + search.
  // "custom" mode treats every cue as eligible (the picker IS the curation step).
  // Categories come dynamically from /api/ronin-box/categories so admin-added categories appear.
  const visibleByCategory = useMemo(() => {
    const q = search.trim().toLowerCase();
    const groups = {};
    const isCustom = mode === "custom";
    for (const c of cues) {
      const isRelevant = isCustom || c.category === "atmosphere" || (c.modes || []).includes(mode);
      if (!isRelevant) continue;
      if (q && !(c.text || "").toLowerCase().includes(q)) continue;
      const k = c.category || "punch";
      (groups[k] = groups[k] || []).push(c);
    }
    return groups;
  }, [cues, mode, search]);

  // Category-relevant ids (used for select-all-in-category)
  const categoryIds = useMemo(() => {
    const map = {};
    const isCustom = mode === "custom";
    for (const c of cues) {
      const isRelevant = isCustom || c.category === "atmosphere" || (c.modes || []).includes(mode);
      if (!isRelevant) continue;
      const k = c.category || "punch";
      (map[k] = map[k] || []).push(c.id);
    }
    return map;
  }, [cues, mode]);

  // Cumulative level filter — wraps idsForLevel for consistent use elsewhere
  const idsAtOrBelowLevel = (lvl) => idsForLevel(cues, lvl);

  const applyLevelPreset = (lvl) => {
    setActiveLevel(lvl);
    if (lvl === "custom") {
      // "Custom" = clean slate. User picks each cue manually from here.
      setSelected(new Set());
    } else {
      setSelected(new Set(idsForLevel(cues, lvl)));
    }
  };

  const allRelevantIds = useMemo(
    () => Object.values(categoryIds).flat(),
    [categoryIds]
  );

  const toggleCue = (id) => {
    // Manual toggle drops us out of any active level preset
    setActiveLevel("custom");
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const setCategoryAll = (cat, on) => {
    // Manual category toggle also drops us out of any active level preset
    setActiveLevel("custom");
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of categoryIds[cat] || []) {
        if (on) next.add(id); else next.delete(id);
      }
      return next;
    });
  };

  const selectAll = () => { setActiveLevel("custom"); setSelected(new Set(allRelevantIds)); };
  const clearAll = () => { setActiveLevel("custom"); setSelected(new Set()); };

  const toggleCollapsed = (cat) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const confirm = () => {
    // Persist & guard: must have at least one PUNCH (or any non-atmosphere) cue for shadow/bag.
    // Otherwise the workout would just play atmosphere lines.
    const persistArr = Array.from(selected);
    try { localStorage.setItem(LS_KEY, JSON.stringify(persistArr)); } catch { /* ignore */ }
    onConfirm(persistArr, cues);
  };

  const selectedCount = selected.size;
  const totalRelevant = allRelevantIds.length;

  // Compute per-category selected counts (for UI badges) — uses dynamic categories from API
  const catStats = useMemo(() => {
    const out = {};
    const catList = (categories.length > 0 ? categories : []).map((c) => c.id);
    // Include any cue categories not in the catalog (defensive fallback)
    for (const id of Object.keys(categoryIds)) {
      if (!catList.includes(id)) catList.push(id);
    }
    for (const cat of catList) {
      const all = categoryIds[cat] || [];
      const sel = all.filter((id) => selected.has(id)).length;
      out[cat] = { sel, total: all.length };
    }
    return out;
  }, [categoryIds, selected, categories]);

  // Validation: at least one non-atmosphere cue selected (otherwise workout is dead)
  const nonAtmosphereSelected = useMemo(() => {
    for (const c of cues) {
      if (c.category === "atmosphere") continue;
      if (selected.has(c.id)) return true;
    }
    return false;
  }, [cues, selected]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="cue-picker-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#27272A] shrink-0">
          <DialogTitle className="font-display text-2xl uppercase tracking-tight text-white">Pick your cues</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Starts fresh every time. Hit a level to bulk-fill, then tick/untick individual cues to fine-tune.
          </DialogDescription>
        </DialogHeader>

        {/* Level preset chips — quick-fill the selection by skill level */}
        <div className="px-6 pt-3 pb-1 border-b border-[#27272A] flex items-center gap-2 flex-wrap shrink-0">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mr-1">Quick select:</span>
          {LEVEL_OPTIONS.map((l) => {
            const isActive = activeLevel === l.id;
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => applyLevelPreset(l.id)}
                className={`px-2.5 py-1 border rounded-sm text-[10px] uppercase tracking-widest font-bold transition-colors ${isActive ? "border-[#FF331F] bg-[#FF331F]/15 text-[#FF331F]" : "border-[#27272A] bg-[#050505] text-zinc-300 hover:text-white hover:border-zinc-700"}`}
                data-testid={`cue-picker-level-${l.id}`}
                aria-pressed={isActive}
              >
                {l.label}
              </button>
            );
          })}
          <span className="text-[10px] text-zinc-600 ml-1">(Custom = blank · others replace your selection)</span>
        </div>

        {/* Search + bulk actions */}
        <div className="px-6 py-3 border-b border-[#27272A] flex items-center gap-3 flex-wrap shrink-0">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search cues…"
              className="pl-9 bg-[#050505] border-[#27272A] text-white rounded-sm h-9"
              data-testid="cue-picker-search"
            />
          </div>
          <Button
            variant="outline" size="sm"
            onClick={selectAll}
            className="border-[#27272A] bg-[#050505] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-[10px] h-9"
            data-testid="cue-picker-select-all"
          >Select all</Button>
          <Button
            variant="outline" size="sm"
            onClick={clearAll}
            className="border-[#27272A] bg-[#050505] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-[10px] h-9"
            data-testid="cue-picker-clear-all"
          >Clear</Button>
        </div>

        {/* Cue grid (scrollable) */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-zinc-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading cues…
            </div>
          ) : hasHydrated && totalRelevant === 0 ? (
            <p className="text-sm text-zinc-500 py-6 text-center">No cues available for this mode yet.</p>
          ) : (
            <div className="space-y-3">
              {(categories.length > 0 ? categories.map((c) => c.id) : Object.keys(catStats)).map((cat) => {
                const list = visibleByCategory[cat] || [];
                const stats = catStats[cat];
                if (!stats || stats.total === 0) return null;
                const catMeta = categories.find((c) => c.id === cat);
                const catLabel = catMeta?.label || cat;
                const isCollapsed = collapsed.has(cat);
                const allChecked = stats.sel === stats.total;
                const someChecked = stats.sel > 0 && stats.sel < stats.total;
                return (
                  <div key={cat} className="border border-[#27272A] rounded-sm bg-[#050505]">
                    {/* Header row */}
                    <div className="flex items-center justify-between px-3 py-2 border-b border-[#27272A] bg-[#0F0F0F]">
                      <div className="flex items-center gap-3 min-w-0">
                        <Checkbox
                          checked={allChecked ? true : someChecked ? "indeterminate" : false}
                          onCheckedChange={(v) => setCategoryAll(cat, !!v)}
                          className="border-zinc-600 data-[state=checked]:bg-[#FF331F] data-[state=checked]:border-[#FF331F]"
                          data-testid={`cue-picker-cat-${cat}`}
                          aria-label={`Toggle all ${catLabel}`}
                        />
                        <button
                          onClick={() => toggleCollapsed(cat)}
                          className="flex items-center gap-2 group"
                          data-testid={`cue-picker-cat-toggle-${cat}`}
                        >
                          {isCollapsed
                            ? <ChevronRight className="h-4 w-4 text-zinc-500 group-hover:text-white" />
                            : <ChevronDown className="h-4 w-4 text-zinc-500 group-hover:text-white" />}
                          <span className="font-display text-sm uppercase tracking-widest text-white">{catLabel}</span>
                        </button>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest font-bold text-[#FF331F]">
                        {stats.sel} / {stats.total}
                      </span>
                    </div>
                    {/* Cue chips */}
                    {!isCollapsed && (
                      <div className="p-2 grid grid-cols-2 sm:grid-cols-3 gap-1">
                        {list.length === 0 ? (
                          <p className="col-span-full text-xs text-zinc-600 px-2 py-3">No matches.</p>
                        ) : (
                          list.map((c) => {
                            const checked = selected.has(c.id);
                            return (
                              <label
                                key={c.id}
                                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-sm border cursor-pointer transition-colors text-sm ${
                                  checked
                                    ? "border-[#FF331F]/60 bg-[#FF331F]/10 text-white"
                                    : "border-[#27272A] bg-[#0A0A0A] text-zinc-400 hover:text-white hover:border-zinc-700"
                                }`}
                                data-testid={`cue-picker-cue-${c.id}`}
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={() => toggleCue(c.id)}
                                  className="border-zinc-600 data-[state=checked]:bg-[#FF331F] data-[state=checked]:border-[#FF331F]"
                                />
                                <span className="truncate">{c.text}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-[#27272A] flex items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-zinc-400">
            <span className="font-display text-base text-white mr-1">{selectedCount}</span>
            / {totalRelevant} cues selected
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => {
                try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
                setSelected(new Set(allRelevantIds));
              }}
              className="border-[#27272A] bg-[#0A0A0A] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-[10px]"
              data-testid="cue-picker-reset"
            >
              <RotateCcw className="h-3 w-3 mr-2" /> Reset to all
            </Button>
            <Button
              onClick={confirm}
              disabled={!nonAtmosphereSelected}
              className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs disabled:opacity-40"
              data-testid="cue-picker-confirm"
            >
              <Play className="h-3.5 w-3.5 mr-2" /> Start workout
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
